import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { isPaidSongRequestForLedger } from '@/lib/payoutLedger';
import {
  getSongInteractionsForSession,
  getKaraokeRequestsForSessionWindow,
  getLegacyGatewayKaraokeRowsFromRequests,
  mergeRequestSnapshotsById,
  pageVisitBelongsToSessionFunnel,
} from '@/lib/sessionFunnelQueries';

function toDate(v) {
  if (!v) return new Date(0);
  return v?.toDate ? v.toDate() : new Date(v);
}

/**
 * Live + ended karaoke session funnel for a single host session.
 * Conversion = paid checkouts (paid karaoke_requests) ÷ paid “basket” (song_interactions added_to_cart for this session).
 */
export async function fetchKaraokeLiveSessionMetrics(artistId, sessionId, sessionStartTime, endTime = new Date()) {
  if (!artistId || !sessionId) {
    return {
      uniqueVisitors: 0,
      totalViews: 0,
      freeSongRequests: 0,
      paidSongsAddedToBasket: 0,
      paidSongsCheckout: 0,
      conversionPct: 0,
    };
  }

  const start = toDate(sessionStartTime);
  const end = toDate(endTime);

  const visitsSnap = await getDocs(query(collection(db, 'page_visits'), where('artist_id', '==', artistId)));
  const sessionVisits = visitsSnap.docs
    .map((d) => d.data())
    .filter((v) => pageVisitBelongsToSessionFunnel(v, sessionId, start, end));

  const uniqueVisitors = new Set(sessionVisits.map((v) => v.visitor_id).filter(Boolean)).size;
  const totalViews = sessionVisits.length;

  const interactions = await getSongInteractionsForSession(artistId, sessionId, start, end);
  const paidSongsAddedToBasket = interactions.filter((i) => i.interaction_type === 'added_to_cart').length;

  const kReq = await getKaraokeRequestsForSessionWindow(artistId, sessionId, start, end);
  const legacy = await getLegacyGatewayKaraokeRowsFromRequests(artistId, sessionId);
  const requests = mergeRequestSnapshotsById(kReq, legacy);
  const freeSongRequests = requests.filter((r) => !isPaidSongRequestForLedger(r)).length;
  const paidSongsCheckout = requests.filter((r) => isPaidSongRequestForLedger(r)).length;

  const conversionPct =
    paidSongsAddedToBasket > 0
      ? parseFloat(((paidSongsCheckout / paidSongsAddedToBasket) * 100).toFixed(2))
      : paidSongsCheckout > 0
        ? 100
        : 0;

  return {
    uniqueVisitors,
    totalViews,
    freeSongRequests,
    paidSongsAddedToBasket,
    paidSongsCheckout,
    conversionPct,
  };
}

/** Fields to merge into `storeSessionHistory` for karaoke sessions. */
export async function buildKaraokeSessionHistoryExtras(artistId, sessionId, sessionStartTime, endTime) {
  const m = await fetchKaraokeLiveSessionMetrics(artistId, sessionId, sessionStartTime, endTime);
  return {
    uniqueVisitors: m.uniqueVisitors,
    totalViews: m.totalViews,
    songsAddedToCart: m.paidSongsAddedToBasket,
    checkouts: m.paidSongsCheckout,
    conversionRate: m.conversionPct,
    karaoke_free_requests: m.freeSongRequests,
    karaoke_paid_cart: m.paidSongsAddedToBasket,
    karaoke_paid_checkouts: m.paidSongsCheckout,
  };
}
